/**
 * `JasenedutFilters` — the `/jasenedut` category-pill + city filter controls
 * (029 US3 / T031 filter increment, research D6: city filter IN, geolocation
 * "Lähin" DEFERRED — see research.md D10).
 *
 * Extracted from `JasenedutRoute` so each file stays under the 400-line
 * architecture ceiling and the filter UI owns its own presentation. This
 * component is PURE PRESENTATION: it renders the controls and reports user
 * intent through `onSelectCategory` / `onSelectCity`; all filter STATE and the
 * `benefit` filtering pipeline live in `JasenedutRoute` (the single owner of
 * the read data).
 *
 *   - Category pills: one `<button>` per {@link CategoryPill}, named by its
 *     label. The first pill is the net-new "Kaikki edut" show-all (its value is
 *     the `allValue` sentinel); the rest are the proto categories, labelled
 *     verbatim from the authored `benefit.category` strings (NOT i18n — only
 *     "Kaikki edut" is a UI label). The active pill carries `aria-pressed`.
 *   - City filter: a NATIVE `<select>` (role combobox), the member-side
 *     convention (Profile / contact-steps use native selects; the wrapped Radix
 *     Select is admin/CMS-only). Rendered ONLY when the caller passes
 *     `cityOptions` (the route shows it only while "Hotellit ja kylpylät" is
 *     active). Its `<option>`s are the caller-DERIVED hotel cities (gate flag C:
 *     clean `cities` membership, never a hardcoded city list), plus an
 *     "all cities" default (empty value).
 */

import { type JSX, useId } from 'react';

import type { MessageKeys } from '../../i18n/messages/index.js';

/** A category filter pill: `value` is the filter key, `label` the button text. */
export interface CategoryPill {
  readonly value: string;
  readonly label: string;
}

export interface JasenedutFiltersProps {
  readonly pills: readonly CategoryPill[];
  readonly activeCategory: string;
  readonly onSelectCategory: (value: string) => void;
  /**
   * The derived hotel-city options. When `null` the city filter is not rendered
   * (the active category is not the hotels category, or no hotel carries a
   * city). When present, the `<select>` shows these plus an "all cities" default.
   */
  readonly cityOptions: readonly string[] | null;
  readonly activeCity: string;
  readonly onSelectCity: (city: string) => void;
  readonly t: (key: MessageKeys) => string;
}

const containerClasses = 'flex flex-col gap-3';
const pillRowClasses = 'flex flex-wrap gap-2';
const pillBaseClasses =
  'rounded-full px-4 py-1.5 text-sm font-medium ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const pillActiveClasses = 'bg-foreground text-background';
const pillInactiveClasses = 'bg-muted text-foreground hover:bg-muted/80';
const cityRowClasses = 'flex items-center gap-2';
const cityLabelClasses = 'text-sm font-medium text-foreground';
const citySelectClasses =
  'rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export function JasenedutFilters({
  pills,
  activeCategory,
  onSelectCategory,
  cityOptions,
  activeCity,
  onSelectCity,
  t,
}: JasenedutFiltersProps): JSX.Element {
  const cityFilterId = useId();

  return (
    <div className={containerClasses}>
      <div className={pillRowClasses}>
        {pills.map((pill) => {
          const isActive = pill.value === activeCategory;
          return (
            <button
              key={pill.value}
              type="button"
              aria-pressed={isActive}
              className={`${pillBaseClasses} ${isActive ? pillActiveClasses : pillInactiveClasses}`}
              onClick={() => {
                onSelectCategory(pill.value);
              }}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {cityOptions !== null ? (
        <div className={cityRowClasses}>
          <label htmlFor={cityFilterId} className={cityLabelClasses}>
            {t('jasenedut.cityFilterLabel')}
          </label>
          <select
            id={cityFilterId}
            className={citySelectClasses}
            value={activeCity}
            onChange={(event) => {
              onSelectCity(event.target.value);
            }}
          >
            <option value="">{t('jasenedut.cityFilterAll')}</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}
